"use client";

import Flex from "@/components/layout/Flex";
import Carousel, { useCarouselStore } from "@/components/media/Carousel";
import Typography from "@/components/shared/Typography";
import Visibility from "@/components/shared/Visibility";
import Button from "@/components/ui/Button";
import Tags from "@/components/ui/Tags";
import { cn } from "@/lib/utils";
import type { GalleryItemProps } from "../types";

function GalleryItem({
  index,
  label = "",
  description = "",
  tags = [],
  url,
  alt,
  href,
  presetRatio = "tall",
  className,
  sm,
  md = "md:basis-1/2",
  lg = "lg:basis-1/3",
  ...props
}: GalleryItemProps) {
  const isSlideInView = useCarouselStore(state => state.slidesInView).has(
    index,
  );

  return (
    <Carousel.Item
      data-slot="gallery-item"
      index={index}
      sm={sm}
      md={md}
      lg={lg}
      className={cn("basis-full h-fit min-h-[300px] rounded-none", className)}
      {...props}
    >
      <Button.Image
        className="p-0"
        presetRatio={presetRatio}
        src={url}
        alt={alt}
        backgroundImageProps={{
          imageClassName: "object-cover rounded-sm",
          foreground: "flex flex-col",
          sizes:
            "(max-width: 640px) 100vw, (max-width: 768px) 100vw, (max-width: 1024px) 50vw, 341px",
          quality: 75,
          crop: "fill",
          loading: "lazy",
        }}
        href={href}
        aria-label="Learn more"
        aria-hidden={!isSlideInView}
        tabIndex={isSlideInView ? 0 : -1}
      >
        <Flex.Column fill className="min-h-1/3" />

        <Flex.Column className="max-h-2/3 p-4 bg-card rounded-bl-sm rounded-br-sm border-border border-t-2 hover:bg-card/80">
          <Typography.H4>{label}</Typography.H4>
          <Typography.Small className="h-full">{description}</Typography.Small>

          <Visibility shouldShow={!!tags}>
            <Tags tags={tags} clickable={false} />
          </Visibility>
        </Flex.Column>
      </Button.Image>
    </Carousel.Item>
  );
}

GalleryItem.displayName = "Gallery.Item";

export default GalleryItem;
